import { FormSection } from '../FormSection';
import {
  TravellerBearerDTO,
  TravellerWithCollectionMembershipsDTO,
  useGetTravellerBearers,
} from '@/api/trafikportalen';
import { FormattedMessage, useIntl } from 'react-intl';
import { DialogWindow } from '../Dialog';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type SetStateAction,
} from 'react';
import { normalizeCardSerial } from '@/helpers/cardSerial';
import { useStudentsDisabled } from '@/contexts/ConfigContext';
import { toast } from '@/hooks/useToast';
import { useSchoolCardSerials } from '@/hooks/useSchoolCardSerials';
import Button from '../core/Button';
import IconButton from '../core/IconButton';
import { IconEdit, IconGraduationCap, IconInfo } from '@/icons';
import { TravelCardCreateForm } from './TravelCardCreateForm';
import { Bearer } from '../Bearer';
import { FavoredBearerAction } from '../FavoredBearerAction';
import { Spinner } from '../core/Spinner';
import { TravellerCardHistory } from './TravellerCardHistory';
import { TravelCardUpdateForm } from './TravelCardUpdateForm';
import { canSetFavoredBearer } from '@/helpers/bearers';
import { PendingCardBadge } from '@/components/PendingCardBadge';
import { hasAnyMembershipDepartmentBearerManagerAccess } from '@/helpers/departmentPermissions';
import { useFavoredBearer } from '@/hooks/useFavoredBearer';
import { usePendingSchoolCard } from '@/hooks/usePendingSchoolCard';

const SchoolCardBadge = () => (
  <span className="inline-flex h-min items-center gap-1 whitespace-nowrap rounded bg-background-400 px-1.5 py-0.5 font-medium text-text-800">
    <IconGraduationCap className="size-4 shrink-0" aria-hidden="true" />
    <FormattedMessage defaultMessage="Skolkort" />
  </span>
);

interface Props {
  traveller: TravellerWithCollectionMembershipsDTO;
}

export const TravellerBearers = ({ traveller }: Props) => {
  const { formatMessage } = useIntl();

  const [addCardOpen, setAddCardOpen] = useState(false);

  const {
    data: travellerBearers,
    isLoading,
    isError: bearersError,
  } = useGetTravellerBearers(traveller.traveller.id);

  useEffect(() => {
    if (bearersError) {
      toast.error(
        {
          title: formatMessage({
            defaultMessage: 'Kunde inte hämta biljettbärare',
          }),
        },
        { autoClose: 3000 },
      );
    }
  }, [bearersError, formatMessage]);

  const { favored_bobcat_bearer_id } = traveller.traveller ?? {};
  const { alltix_bearers, private_bearers } = travellerBearers ?? {};
  const hasPrivateEmail = Boolean(traveller.traveller.bobcat_private_email);
  const canManageCards = Boolean(traveller.traveller.bobcat_alltix_id);
  const canManageBearers =
    hasAnyMembershipDepartmentBearerManagerAccess(traveller);
  const { setFavoredBearer, getFavoredBearerErrorMessage, isPending } =
    useFavoredBearer({ travellerId: traveller.traveller.id });
  const [pendingBearerId, setPendingBearerId] = useState<string | null>(null);
  const isSettingFavored = useRef(false);
  const pendingSchoolCard = usePendingSchoolCard(traveller.memberships);

  const { schoolCardSerials } = useSchoolCardSerials(
    alltix_bearers?.map((bearer) => bearer.serial) ?? [],
  );

  const isSchoolCard = useCallback(
    (serial?: string) =>
      !!serial && schoolCardSerials.has(normalizeCardSerial(serial)),
    [schoolCardSerials],
  );

  const handleSetFavored = useCallback(
    async (bearerId: string) => {
      if (isSettingFavored.current) return;
      isSettingFavored.current = true;
      setPendingBearerId(bearerId);
      try {
        await setFavoredBearer(bearerId);
      } catch (error) {
        toast.error(
          {
            title: formatMessage({ defaultMessage: 'Ett fel inträffade' }),
            description: getFavoredBearerErrorMessage(error),
          },
          { autoClose: 3000 },
        );
      } finally {
        isSettingFavored.current = false;
        setPendingBearerId(null);
      }
    },
    [formatMessage, getFavoredBearerErrorMessage, setFavoredBearer],
  );

  const alltixBearersContent = useMemo(() => {
    if (isLoading) {
      return <Spinner />;
    }

    const pendingCard = pendingSchoolCard && (
      <div className="mb-2 flex flex-wrap items-center gap-2 border-b border-b-background-200 pb-2 last:border-b-0">
        <b>
          <FormattedMessage defaultMessage="Skolkort" />
        </b>
        <PendingCardBadge state={pendingSchoolCard} />
      </div>
    );

    const systemBearers = alltix_bearers ?? [];

    if (systemBearers.length === 0) {
      return (
        pendingCard || (
          <i className="pb-2 text-text-700">
            <FormattedMessage defaultMessage="Det finns inga systemägda resekort på den här resenären." />
          </i>
        )
      );
    }

    const bearers = systemBearers.map((bearer) => {
      const isFavored = bearer.id === favored_bobcat_bearer_id;
      return (
        <TravelCard
          key={bearer.id}
          bearer={bearer}
          travellerId={traveller.traveller.id}
          isFavored={isFavored}
          isSchoolCard={isSchoolCard(bearer.serial)}
          canManageBearers={canManageBearers}
          isSettingFavored={isPending && pendingBearerId === bearer.id}
          onSetFavored={handleSetFavored}
        />
      );
    });

    return (
      <>
        {pendingCard}
        {bearers}
      </>
    );
  }, [
    canManageBearers,
    favored_bobcat_bearer_id,
    handleSetFavored,
    isLoading,
    isPending,
    isSchoolCard,
    pendingBearerId,
    pendingSchoolCard,
    alltix_bearers,
    traveller.traveller.id,
  ]);

  const privateBearersContent = useMemo(() => {
    if (!hasPrivateEmail) {
      return (
        <p className="pb-2 italic text-text-700">
          <FormattedMessage defaultMessage="'E-post i appen / Mina sidor' är inte angivet. Privata bärare syns först när kontot är kopplat." />
        </p>
      );
    }

    if (isLoading) {
      return <Spinner />;
    }

    if (!private_bearers?.length) {
      return (
        <p className="pb-2 italic text-text-700">
          <FormattedMessage defaultMessage="E-posten tillhör inte en profil i reseappen / Mina sidor, eller så finns det inga bärare kopplade till den." />
        </p>
      );
    }

    return private_bearers.map((bearer) => {
      const isFavored = bearer.id === favored_bobcat_bearer_id;
      return (
        <div
          key={bearer.id}
          className="flex items-center gap-2 border-b border-b-background-200 pb-2 last:border-b-0"
        >
          <Bearer bearer={bearer} />
          <FavoredBearerAction
            bearer={bearer}
            isFavored={isFavored}
            canSet={canManageBearers && canSetFavoredBearer(bearer)}
            isPending={isPending && pendingBearerId === bearer.id}
            onSetFavored={handleSetFavored}
          />
        </div>
      );
    });
  }, [
    canManageBearers,
    favored_bobcat_bearer_id,
    handleSetFavored,
    hasPrivateEmail,
    isLoading,
    isPending,
    pendingBearerId,
    private_bearers,
  ]);

  return (
    <>
      <FormSection
        title={
          <div className="flex items-center gap-2">
            <FormattedMessage defaultMessage="Biljettbärare" />
            <DialogWindow
              title={formatMessage({ defaultMessage: 'Biljettbärare' })}
              content={
                <div className="flex flex-col gap-2">
                  <p>
                    <FormattedMessage defaultMessage="Biljettbärare kallas det som en biljett kopplas till när den ställs ut." />
                  </p>
                  <p>
                    <FormattedMessage defaultMessage="Systemägda resekort är kort som ägs av systemet." />
                  </p>
                  <p>
                    <FormattedMessage defaultMessage="Genom att ange 'E-post i appen / Mina sidor' hämtas privata bärare ut och kan nyttjas." />
                  </p>
                  <p>
                    <FormattedMessage defaultMessage="Föredragen bärare är den bärare som automatiskt utställda biljetter kopplas till. Den blir även vald i första hand när manuell biljett ska ställas ut." />
                  </p>
                  <p>
                    <FormattedMessage defaultMessage="Ett skolkort kan bara bytas mot ett ledigt kort, till exempel om det har kommit bort." />
                  </p>
                  <p>
                    <FormattedMessage defaultMessage="Korthistorik visar bara systemägda resekort, både nuvarande och tidigare. Privata kort från reseappen ingår inte. Siffran på knappen är hur många kort som har frisläppts." />
                  </p>
                </div>
              }
              dialogTrigger={
                <IconButton
                  aria-label={formatMessage({
                    defaultMessage: 'Visa information om biljettbärare',
                  })}
                  icon={<IconInfo />}
                  variant="ghost"
                />
              }
            />
          </div>
        }
      >
        <div className="mb-5 flex flex-col gap-2">
          <b className="text-text-800">
            <FormattedMessage defaultMessage="Från resenärens privata konto (hämtat via 'E-post i appen / Mina sidor'):" />
          </b>
          {privateBearersContent}
        </div>
        {canManageBearers && !canManageCards && (
          <p className="text-text-700">
            <FormattedMessage defaultMessage="Kort kan inte hanteras ännu. Resenären är inte klar för korthantering." />
          </p>
        )}
        <div className="flex flex-col gap-2">
          <b className="mb-3 text-text-800">
            <FormattedMessage defaultMessage="Systemägda resekort:" />
          </b>
          <div className="flex flex-col gap-2">{alltixBearersContent}</div>
          <div className="flex flex-wrap items-center gap-2">
            {canManageBearers && (
              <Button
                onClick={() => setAddCardOpen(true)}
                disabled={!canManageCards}
              >
                <FormattedMessage defaultMessage="Lägg till systemägt resekort" />
              </Button>
            )}
          </div>
          <TravellerCardHistory travellerId={traveller.traveller.id} />
        </div>
      </FormSection>
      <DialogWindow
        title={formatMessage({
          defaultMessage: 'Lägg till systemägt resekort på resenären',
        })}
        content={
          <TravelCardCreateForm
            onOpenChange={setAddCardOpen}
            traveller={traveller}
            onSuccess={() => setAddCardOpen(false)}
          />
        }
        onOpenChange={setAddCardOpen}
        open={addCardOpen}
      />
    </>
  );
};

interface TravelCardProps {
  bearer: TravellerBearerDTO;
  travellerId: string;
  isFavored?: boolean;
  isSchoolCard?: boolean;
  canManageBearers?: boolean;
  isSettingFavored?: boolean;
  onSetFavored: (bearerId: string) => void;
}

const TravelCard = ({
  bearer,
  travellerId,
  isFavored,
  isSchoolCard = false,
  canManageBearers = false,
  isSettingFavored = false,
  onSetFavored,
}: TravelCardProps) => {
  const { formatMessage } = useIntl();
  const studentsDisabled = useStudentsDisabled();
  const showSchoolCard = isSchoolCard && !studentsDisabled;
  const [editOpen, setEditOpen] = useState(false);
  const [replacingSchoolCard, setReplacingSchoolCard] = useState(false);
  const canEdit = Boolean(bearer.serial) && canManageBearers;

  const handleEditOpenChange = useCallback((open: SetStateAction<boolean>) => {
    setEditOpen(open);
    if (open === false) {
      setReplacingSchoolCard(false);
    }
  }, []);

  const handleReplacingChange = useCallback((replacing: boolean) => {
    setReplacingSchoolCard(replacing);
  }, []);

  return (
    <div className="mb-2 flex flex-wrap items-center justify-between gap-2 border-b border-b-background-200 pb-2 last:border-b-0">
      <div className="flex flex-1 flex-wrap items-center gap-2">
        <Bearer bearer={bearer} />
        {showSchoolCard && <SchoolCardBadge />}
      </div>
      <div className="ml-auto flex items-center gap-1">
        <FavoredBearerAction
          bearer={bearer}
          isFavored={!!isFavored}
          canSet={canManageBearers && canSetFavoredBearer(bearer)}
          isPending={isSettingFavored}
          onSetFavored={onSetFavored}
        />
        {canEdit && (
          <IconButton
            aria-label={formatMessage({ defaultMessage: 'Redigera' })}
            icon={<IconEdit />}
            variant="ghost"
            onClick={() => setEditOpen(true)}
          />
        )}
      </div>
      {canEdit && bearer.serial && (
        <DialogWindow
          title={
            replacingSchoolCard
              ? formatMessage({ defaultMessage: 'Byt skolkort' })
              : formatMessage({ defaultMessage: 'Redigera kort' })
          }
          content={
            <TravelCardUpdateForm
              travellerId={travellerId}
              serial={bearer.serial}
              currentLabel={bearer.name}
              isSchoolCard={showSchoolCard}
              onOpenChange={handleEditOpenChange}
              onReplacingChange={handleReplacingChange}
            />
          }
          onOpenChange={handleEditOpenChange}
          open={editOpen}
        />
      )}
    </div>
  );
};
